import React, { useState, useRef, useEffect } from 'react';

const OnboardingTooltip = ({ 
  children, 
  content, 
  position = 'top', // 'top', 'bottom', 'left', 'right'
  trigger = 'hover', // 'hover', 'click', 'focus', 'manual'
  isVisible = false,
  onVisibilityChange,
  delay = 200,
  className="",
  tooltipClassName = "",
  arrow = true,
  maxWidth = 'max-w-xs'
}) => {
  const [showTooltip, setShowTooltip] = useState(isVisible);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  const isControlled = onVisibilityChange !== undefined;
  const visible = isControlled ? isVisible : showTooltip;

  useEffect(() => {
    if (visible && triggerRef.current && tooltipRef.current) {
      updateTooltipPosition();
    }
  }, [visible, content]);

  useEffect(() => {
    if (isControlled) {
      setShowTooltip(isVisible);
    }
  }, [isVisible, isControlled]);

  const updateTooltipPosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - 8;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + 8;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - 8;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + 8;
        break;
    }

    // Adjust for viewport boundaries
    if (left < 8) left = 8;
    if (left + tooltipRect.width > viewportWidth - 8) {
      left = viewportWidth - tooltipRect.width - 8;
    }
    if (top < 8) top = 8;
    if (top + tooltipRect.height > viewportHeight - 8) {
      top = viewportHeight - tooltipRect.height - 8;
    }

    setTooltipPosition({ top, left });
  };

  const showTooltipWithDelay = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (isControlled) {
        onVisibilityChange?.(true);
      } else {
        setShowTooltip(true);
      }
    }, delay);
  };

  const hideTooltipWithDelay = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (isControlled) {
        onVisibilityChange?.(false);
      } else {
        setShowTooltip(false);
      }
    }, 100);
  };

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      showTooltipWithDelay();
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      hideTooltipWithDelay();
    }
  };

  const handleClick = () => {
    if (trigger === 'click') {
      if (isControlled) {
        onVisibilityChange?.(!isVisible);
      } else {
        setShowTooltip(!showTooltip);
      }
    }
  };

  const handleFocus = () => {
    if (trigger === 'focus') {
      showTooltipWithDelay();
    }
  };

  const handleBlur = () => {
    if (trigger === 'focus') {
      hideTooltipWithDelay();
    }
  };

  const getArrowStyles = () => {
    const arrowSize = 6;
    const arrowStyles = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid'
    };

    switch (position) {
      case 'top':
        return {
          ...arrowStyles,
          top: '100%',
          left: '50%',
          marginLeft: -arrowSize,
          borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
          borderColor: '#374151 transparent transparent transparent'
        };
      case 'bottom':
        return {
          ...arrowStyles,
          bottom: '100%',
          left: '50%',
          marginLeft: -arrowSize,
          borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
          borderColor: 'transparent transparent #374151 transparent'
        };
      case 'left':
        return {
          ...arrowStyles,
          left: '100%',
          top: '50%',
          marginTop: -arrowSize,
          borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
          borderColor: 'transparent transparent transparent #374151'
        };
      case 'right':
        return {
          ...arrowStyles,
          right: '100%',
          top: '50%',
          marginTop: -arrowSize,
          borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
          borderColor: 'transparent #374151 transparent transparent'
        };
      default:
        return arrowStyles;
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        className={`inline-block ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {children}
      </div>

      {visible && (
        <>
          {/* Backdrop for click trigger */}
          {trigger === 'click' && (
            <div
              className="fixed inset-0 z-40"
              onClick={() => {
                if (isControlled) {
                  onVisibilityChange?.(false);
                } else {
                  setShowTooltip(false);
                }
              }}
            />
          )}
          
          {/* Tooltip */}
          <div
            ref={tooltipRef}
            className={`
              fixed z-50 px-3 py-2 text-sm text-white bg-gray-700 rounded-lg shadow-lg
              transition-opacity duration-200 pointer-events-none
              ${maxWidth} ${tooltipClassName}
            `}
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              opacity: visible ? 1 : 0
            }}
          >
            {content}
            {arrow && <div style={getArrowStyles()} />}
          </div>
        </>
      )}
    </>
  );
};

// Predefined tooltip variants for common onboarding scenarios
export const HelpTooltip = ({ children, content, ...props }) => (
  <OnboardingTooltip
    content={content}
    position="top"
    trigger="hover"
    {...props}
  >
    <div className="inline-flex items-center">
      {children}
      <svg className="w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help dark:text-gray-300 dark:text-gray-600" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
      </svg>
    </div>
  </OnboardingTooltip>
);

export const InfoTooltip = ({ children, content, ...props }) => (
  <OnboardingTooltip
    content={content}
    position="top"
    trigger="hover"
    tooltipClassName="bg-blue-600"
    {...props}
  >
    <div className="inline-flex items-center">
      {children}
      <svg className="w-4 h-4 ml-1 text-blue-500 hover:text-blue-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    </div>
  </OnboardingTooltip>
);

export const WarningTooltip = ({ children, content, ...props }) => (
  <OnboardingTooltip
    content={content}
    position="top"
    trigger="hover"
    tooltipClassName="bg-yellow-600"
    {...props}
  >
    <div className="inline-flex items-center">
      {children}
      <svg className="w-4 h-4 ml-1 text-yellow-500 hover:text-yellow-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    </div>
  </OnboardingTooltip>
);

// Interactive tooltip for guided tours
export const GuidedTooltip = ({ 
  children, 
  content, 
  step, 
  totalSteps, 
  onNext, 
  onPrev, 
  onSkip,
  isVisible,
  onVisibilityChange,
  ...props 
}) => {
  const tooltipContent = (
    <div className="space-y-3">
      <div className="text-sm">{content}</div>
      
      <div className="flex items-center justify-between pt-2 border-t border-gray-600">
        <span className="text-xs text-gray-300 dark:text-gray-600">
          {step} of {totalSteps}
        </span>
        
        <div className="flex space-x-2">
          {step > 1 && (
            <button
              onClick={onPrev}
              className="px-2 py-1 text-xs bg-gray-600 hover:bg-gray-500 rounded transition-colors dark:bg-gray-900"
            >
              Previous
            </button>
          )}
          
          <button
            onClick={onNext}
            className="px-2 py-1 text-xs bg-teal-600 hover:bg-teal-500 rounded transition-colors"
          >
            {step === totalSteps ? 'Finish' : 'Next'}
          </button>
          
          <button
            onClick={onSkip}
            className="px-2 py-1 text-xs text-gray-300 hover:text-white transition-colors dark:text-gray-600 dark:text-white"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <OnboardingTooltip
      content={tooltipContent}
      trigger="manual"
      isVisible={isVisible}
      onVisibilityChange={onVisibilityChange}
      maxWidth="max-w-sm"
      tooltipClassName="bg-gray-800"
      {...props}
    >
      {children}
    </OnboardingTooltip>
  );
};

export default OnboardingTooltip;
